import { Component, inject, OnInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReferralActions } from 'src/app/store/referral/referral.action';
import { ReferralState } from 'src/app/store/referral/referral.state';
import { Clipboard } from '@angular/cdk/clipboard';
import { GenericState } from 'src/app/store/generic.state';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Router } from '@angular/router';
import { GenericService } from 'src/app/services/generic.service';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { DatePipe } from '@angular/common';

@Component({
	selector: 'app-referrals',
	templateUrl: './referrals.component.html',
	styleUrls: ['./referrals.component.scss'],
	imports: [
		NzDropDownModule,
		NzSpaceModule,
		NzButtonModule,
		NzIconModule,
		NzMenuModule,
		NzInputModule,
		FormsModule,
		NzPaginationModule,
		NzModalModule,
		DatePipe
	]
})
export class ReferralsComponent implements OnInit {
	private readonly nzMessage = inject(NzMessageService);
	private readonly store = inject(Store);
	private readonly destroyRef = inject(DestroyRef);
	private readonly clipboard = inject(Clipboard);
	private readonly notification = inject(NzNotificationService);
	private readonly router = inject(Router);
	private readonly genericService = inject(GenericService);

	private readonly genericResult$: Observable<any> = this.store.select(
		GenericState.getGeneric
	);
	public userRole: any;

	private readonly referralsResult$: Observable<any> = this.store.select(
		ReferralState.getReferral
	);

	public referrals: any[];
	public referralCount: number;

	public searchText = new Subject<string>();
	public searchKeyword = '';
	public pageIndex = 1;
	public pageSize = 25;
	public readonly pageSizeOption: number[] = [25, 50, 75, 100];

	public isInviteModalVisible = false;

	public baseUrl: string;

	public firstLoad = true;
	private defaultRefId: number;

	constructor() {
		this.searchText
			.pipe(debounceTime(500), distinctUntilChanged())
			.subscribe(() => {
				this.search();
			});
	}

	ngOnInit(): void {
		this.genericResult$
			?.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				this.userRole = data.userRole;
				if (this.userRole && this.firstLoad) {
					this.checkCloudstaffEmail();
					this.firstLoad = false;
				}
			});
		this.referralsResult$
			?.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				this.referrals = data.referral;
				this.referralCount = data.count;
			});
		this.baseUrl = environment.identity.msalConfig.redirectUri;
		this.defaultRefId = environment.defaultReferralId;
		this.search();
	}

	private checkCloudstaffEmail(): void {
		if (!this.userRole.email.includes('@cloudstaff.com')) {
			this.notification.blank(
				'Referral Program',
				'You must use your Cloudstaff email to participate in the referral program.'
			);
			this.router.navigate(['/']);
		}
	}

	public getProfilePix(image: string): string {
		if (image?.includes('no-avatar') || image === '') {
			return 'assets/images/blank-image.png';
		} else {
			return image;
		}
	}

	public getStatus(status: string) {
		if (status.toLowerCase() === 'applied') {
			return '#ffad33';
		} else if (status.toLowerCase() === 'hired') {
			return '#66c36a';
		} else {
			return '#4ba9ff';
		}
	}

	private search() {
		this.store.dispatch(
			new ReferralActions.GetReferral({
				searchText: this.searchKeyword,
				pageIndex: this.pageIndex,
				pageSize: this.pageSize
			})
		);
	}
	public pageIndexChanged(event: number): void {
		this.pageIndex = event;
		this.search();
	}

	public pageSizeChanged(size: number, pageIndex: any): void {
		this.pageSize = size;
		setTimeout(() => {
			if (pageIndex === this.pageIndex) {
				this.search();
			}
		}, 100);
	}
	public invite() {
		this.isInviteModalVisible = true;
	}

	public handleCancel() {
		this.isInviteModalVisible = false;
	}
	public handleOk() {
		this.isInviteModalVisible = false;
		this.clipboard.copy(
			this.baseUrl + `?ref=${this.defaultRefId}&id=` + this.userRole.profileId
		);
		this.nzMessage.success('Copied to clipboard ');
	}

	public async export() {
		if (this.referralCount === 0) {
			this.nzMessage.info('No referrals to export');
			return;
		}

		const id = this.nzMessage.loading('Exporting...', {
			nzDuration: 0
		}).messageId;

		const payload = {
			searchText: this.searchKeyword,
			pageIndex: 1,
			pageSize: 1000,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
		};

		this.genericService
			.post(`ApplicantProtected/ExportMyReferrals`, payload)
			.subscribe({
				next: (url) => {
					this.nzMessage.remove(id);
					const fileUrl = encodeURIComponent(url);
					window.open(
						`https://view.officeapps.live.com/op/view.aspx?src=${fileUrl}`,
						'_blank'
					);
				},
				error: () => {
					this.nzMessage.remove(id);
					this.nzMessage.error('Failed to export referrals');
				},
				complete: () => {
					this.nzMessage.remove(id);
				}
			});
	}

	public claim() {
		console.log('Claim');
	}

	public addSpaces(text: any) {
		return text.replace(/,/g, ', ');
	}
}
